<script lang="ts" module>
	import type { HTMLAnchorAttributes, HTMLButtonAttributes } from 'svelte/elements'

	export type ButtonVariant = 'primary' | 'secondary' | 'plain' | 'primary-icon' | 'secondary-icon'
	export type ButtonColor = 'red' | 'orange' | 'green' | 'blue' | 'grey' | 'color-cd' | 'white'

	export type ButtonProps = (HTMLButtonAttributes | HTMLAnchorAttributes) & {
		variant?: ButtonVariant
		color?: ButtonColor
		ref?: HTMLElement | null
		href?: string
		type?: 'button' | 'submit' | 'reset'
		width?: string
		fontSize?: string
		borderRadius?: string
		titleAttr?: string
		disabled?: boolean
	}
</script>

<script lang="ts">
	let {
		class: className,
		variant = 'primary',
		color = 'green',
		ref = $bindable(null),
		href = undefined,
		type = 'button',
		disabled,
		children,
		width,
		fontSize = '0.75rem',
		borderRadius = '0.125rem',
		titleAttr,
		...restProps
	}: ButtonProps = $props()

	const baseClass = $derived(`${variant}-button`)
	const colorClass = $derived(
		variant === 'primary' || variant === 'primary-icon' ? `btn-${color}` : color
	)
</script>

{#if href}
	<a
		bind:this={ref}
		class={[baseClass, colorClass, className ?? '', disabled && 'disabled']}
		{disabled}
		aria-disabled={disabled}
		{href}
		style:--btn-font-size={fontSize}
		style:--btn-border-radius={borderRadius}
		style:--btn-width={width}
		{...restProps}
	>
		{@render children?.()}
	</a>
{:else}
	<button
		bind:this={ref}
		class={[`${baseClass}`, `${colorClass}`, className && `${className}`]}
		{type}
		{disabled}
		aria-disabled={disabled}
		title={titleAttr ?? undefined}
		style:--btn-font-size={fontSize}
		style:--btn-border-radius={borderRadius}
		style:--btn-width={width}
		{...restProps}
	>
		{@render children?.()}
	</button>
{/if}

<style lang="scss">
	@use '$styles/global' as *;
	@use '$styles/buttons' as *;

	.primary-button,
	.primary-icon-button {
		font-family: Roboto, sans-serif;
		font-size: 12px;
		font-style: normal;
		font-weight: 400;
		line-height: 18px;
		border: none;
		color: white;
		padding: 0.375rem 0.5rem;
		border-radius: var(--btn-border-radius, 0.125rem);
		width: var(--btn-width, unset);
		min-width: min-content;
		height: 32px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		cursor: pointer;
		text-decoration: none;
		fill: white;

		&.btn-red {
			background-color: var(--color-red-500);

			&:hover:not(:disabled) {
				background-color: var(--color-red-600);
			}
		}

		&.btn-orange {
			background-color: var(--color-orange-500);

			&:hover:not(:disabled) {
				background-color: var(--color-orange-600);
			}
		}

		&.btn-green {
			background-color: $primaryGreen;

			&:hover:not(:disabled) {
				background-color: var(--color-green-600);
			}
		}

		&.btn-blue {
			background-color: var(--color-blue-500);

			&:hover:not(:disabled) {
				background-color: var(--color-blue-600);
			}
		}

		&.btn-grey {
			background-color: var(--color-slate-500);

			&:hover:not(:disabled) {
				background-color: var(--color-slate-600);
			}
		}

		&.btn-color-cd {
			background-color: $primaryColor;

			&:hover:not(:disabled) {
				background-color: $secondaryColor;
			}
		}

		&.white {
			background-color: white;
			fill: var(--color-gray-700);

			&:hover:not(:disabled) {
				fill: black;
			}
		}

		&:disabled {
			cursor: not-allowed;
			background-color: var(--color-blueish-300);
		}
	}

	.secondary-button,
	.secondary-icon-button {
		font-family: Roboto, sans-serif;
		font-size: 12px;
		font-style: normal;
		font-weight: 400;
		line-height: 18px;
		background-color: white;
		padding: 0.375rem 0.5rem;
		border-radius: var(--btn-border-radius);
		min-width: min-content;
		width: var(--btn-width, unset);
		height: 2rem;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		cursor: pointer;
		text-decoration: none;

		&.green {
			border: var(--btn-border-width, 2px) solid var(--btn-border-color, $primaryColor);

			&:hover:not(:disabled) {
				border-color: var(--color-green-600);
			}
		}

		&.grey {
			border: var(--btn-border-width, 2px) solid var(--btn-border-color, var(--color-slate-500));

			&:hover:not(:disabled) {
				border-color: var(--color-slate-600);
			}
		}

		&.red {
			border: var(--btn-border-width, 2px) solid var(--btn-border-color, var(--color-red-500));

			&:hover:not(:disabled) {
				border-color: var(--color-red-600);
			}
		}

		&.orange {
			border: var(--btn-border-width, 2px) solid var(--btn-border-color, var(--color-orange-500));

			&:hover:not(:disabled) {
				border-color: var(--color-orange-600);
			}
		}

		&.blue {
			border: var(--btn-border-width, 2px) solid var(--btn-border-color, var(--color-blue-500));

			&:hover:not(:disabled) {
				border-color: var(--color-blue-600);
			}
		}

		// FIXME: duplicate
		&.blue {
			border: var(--btn-border-width, 2px) solid var(--btn-border-color, $primaryColor);

			&:hover:not(:disabled) {
				border-color: $secondaryColor;
			}
		}

		&.white {
			border: none;
			box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.25);
			color: var(--color-gray-700);
			transition: color 0.1s ease;

			&:hover:not(:disabled) {
				color: #000;
			}
		}

		&:disabled {
			@include roboto-font(12px, 500, var(--btn-font-size), var(--color-blueish-300));

			border: var(--btn-border-width, 2px) solid var(--btn-border-color, var(--color-blueish-300));
			cursor: not-allowed;
			fill: var(--color-blueish-300);
		}
	}

	.primary-icon-button,
	.secondary-icon-button {
		padding: 0;
		height: max-content;
	}

	.plain-button {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: var(--btn-gap, 0);
		min-width: var(--btn-width, 28px);
		min-height: var(--btn-height, 28px);
		max-width: var(--btn-width, 28px);
		max-height: var(--btn-height, 28px);
		background: var(--btn-background, transparent);
		padding: var(--btn-padding, 0);
		border: var(--btn-border-width, 0) solid var(--btn-border-color, $primaryColor);
		border-radius: var(--btn-border-radius, 0);
		color: var(--btn-color, var(--color-gray-500));
	}
</style>
